import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import type { ModuleKey, PageKey } from '@erp/rpc';
import type { Message, MessageToolResult } from '@/context/agent-state.context';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
import { layerAgentModuleConfig } from './layerAgentModuleConfig';

export interface AgentModuleConfig {
  moduleKey: ModuleKey;
  /**
   * Optional page-level identifier resolved against the backend page
   * registry. Layered on top of the module to provide page-specific prompt
   * fragments and tools.
   */
  pageKey?: PageKey;
  currentPage: string;
  getModuleContext?: () => Record<string, unknown> | undefined;
  renderToolResult?: (
    message: Message,
    toolResult: MessageToolResult,
  ) => ReactNode;
  /** Frontend-defined tools — schemas sent to backend, execution on client */
  tools?: FrontendToolDefinition[];
  placeholder?: string;
  /** Optional notice displayed beside the chat composer. */
  inputNotice?: string;
  headerTitle?: string;
  headerIcon?: ReactNode;
  onFocusNodes?: (nodeIds: string[]) => void;
  /** Allow a module-specific feature flag to expose the shared panel shell. */
  forcePanelEnabled?: boolean;
  /** The module supplies its own chat UI in the shared panel. */
  customRenderer?: boolean;
  /** Human page title for the panel's context line ("Sales order"). */
  surfaceTitle?: string;
  /** The on-screen record, when the page is about one. */
  entity?: { kind: string; id: string; label: string };
  /**
   * Identity of the record the page's tools edit (e.g. `workbook:<id>`). A turn
   * resumed after a refresh runs its pending tool calls only on the page it
   * started on.
   */
  scope?: string;
  /** Opening prompts shown as chips while the thread is empty. Already translated. */
  suggestions?: string[];
}

interface AgentContainerContextData {
  containerElement: HTMLElement | null;
  /** The page-registered module, if the current page registered one. */
  moduleConfig: AgentModuleConfig | null;
  /** Ref-based accessors for callbacks that must be fresh at call-time */
  moduleConfigRef: React.RefObject<AgentModuleConfig | null>;
  /**
   * Layout-level base module — the always-available Nami surface. Used alone
   * when no page registered a module, and layered under the page's module
   * otherwise. A second slot rather than the same one so page registrations
   * and the base never race each other's clear.
   */
  defaultModuleConfig: AgentModuleConfig | null;
  /**
   * The page module layered over the default (`layerAgentModuleConfig`) —
   * what the renderer actually uses.
   */
  effectiveModuleConfig: AgentModuleConfig | null;
  /**
   * The same resolution read at call time, for handlers that must see the
   * latest registration (a send fired before the state update landed).
   */
  getEffectiveModuleConfig: () => AgentModuleConfig | null;
  registerContainer: (element: HTMLElement) => void;
  unregisterContainer: () => void;
  setModuleConfig: (config: AgentModuleConfig) => void;
  clearModuleConfig: () => void;
  setDefaultModuleConfig: (config: AgentModuleConfig) => void;
  clearDefaultModuleConfig: () => void;
}

const AgentContainerContext = createContext<
  AgentContainerContextData | undefined
>(undefined);

export function AgentContainerProvider({ children }: PropsWithChildren) {
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(
    null,
  );
  const [moduleConfig, setModuleConfigState] =
    useState<AgentModuleConfig | null>(null);
  const moduleConfigRef = useRef<AgentModuleConfig | null>(null);
  const [defaultModuleConfig, setDefaultModuleConfigState] =
    useState<AgentModuleConfig | null>(null);
  const defaultModuleConfigRef = useRef<AgentModuleConfig | null>(null);
  const effectiveModuleConfig = layerAgentModuleConfig(
    moduleConfig,
    defaultModuleConfig,
  );
  // Reads the refs only when called (from event handlers), never in render.
  const getEffectiveModuleConfig = () =>
    layerAgentModuleConfig(
      moduleConfigRef.current,
      defaultModuleConfigRef.current,
    );

  const registerContainer = useCallback((element: HTMLElement) => {
    setContainerElement(element);
  }, []);

  const unregisterContainer = useCallback(() => {
    setContainerElement(null);
  }, []);

  const setModuleConfig = useCallback((config: AgentModuleConfig) => {
    moduleConfigRef.current = config;
    setModuleConfigState(config);
  }, []);

  const clearModuleConfig = useCallback(() => {
    moduleConfigRef.current = null;
    setModuleConfigState(null);
  }, []);

  const setDefaultModuleConfig = useCallback((config: AgentModuleConfig) => {
    defaultModuleConfigRef.current = config;
    setDefaultModuleConfigState(config);
  }, []);

  const clearDefaultModuleConfig = useCallback(() => {
    defaultModuleConfigRef.current = null;
    setDefaultModuleConfigState(null);
  }, []);

  return (
    <AgentContainerContext.Provider
      value={{
        containerElement,
        moduleConfig,
        moduleConfigRef,
        defaultModuleConfig,
        effectiveModuleConfig,
        getEffectiveModuleConfig,
        registerContainer,
        unregisterContainer,
        setModuleConfig,
        clearModuleConfig,
        setDefaultModuleConfig,
        clearDefaultModuleConfig,
      }}
    >
      {children}
    </AgentContainerContext.Provider>
  );
}

export function useAgentContainer() {
  const context = useContext(AgentContainerContext);
  if (context === undefined) {
    throw new Error(
      'useAgentContainer must be used within an AgentContainerProvider',
    );
  }
  return context;
}
